import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  createWhatsAppOrderApi,
  getAllOrdersApi,
  getOrderByIdApi,
  updateOrderStatusApi,
  updateOrderApi,
  deleteOrderApi,
  getOrderStatsApi,
  getOrdersByDateRangeApi
} from '../../api/orderApi';

export const createWhatsAppOrder = createAsyncThunk(
  'order/createWhatsAppOrder',
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await createWhatsAppOrderApi(orderData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchAllOrders = createAsyncThunk(
  'order/fetchAllOrders',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await getAllOrdersApi(filters);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchOrderById = createAsyncThunk(
  'order/fetchOrderById',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await getOrderByIdApi(orderId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const changeOrderStatus = createAsyncThunk(
  'order/changeOrderStatus',
  async ({ orderId, status }, { rejectWithValue }) => {
    try {
      const response = await updateOrderStatusApi(orderId, status);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateOrderDetails = createAsyncThunk(
  'order/updateOrderDetails',
  async ({ orderId, data }, { rejectWithValue }) => {
    try {
      const response = await updateOrderApi(orderId, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const cancelOrder = createAsyncThunk(
  'order/cancelOrder',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await deleteOrderApi(orderId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchOrderStats = createAsyncThunk(
  'order/fetchOrderStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getOrderStatsApi();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchOrdersByDateRange = createAsyncThunk(
  'order/fetchOrdersByDateRange',
  async ({ startDate, endDate }, { rejectWithValue }) => {
    try {
      const response = await getOrdersByDateRangeApi(startDate, endDate);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  orders: [],
  currentOrder: null,
  stats: null,
  loading: false,
  error: null,
  success: false,
  totalOrders: 0,
  totalPages: 1,
  currentPage: 1,
  filters: {
    status: '',
    orderType: '',
    startDate: '',
    endDate: '',
    search: '',        // ✅ SEARCH FIELD ADDED
    page: 1,
    limit: 10
  }
};

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    clearError:        (s) => { s.error = null; },
    clearSuccess:      (s) => { s.success = false; },
    setFilters:        (s, a) => { 
      s.filters = { ...s.filters, ...a.payload }; 
    },
    resetFilters:      (s) => { s.filters = initialState.filters; },
    clearCurrentOrder: (s) => { s.currentOrder = null; },
    resetOrderState:   () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Create WhatsApp Order
      .addCase(createWhatsAppOrder.pending,    (s) => { s.loading = true; s.error = null; s.success = false; })
      .addCase(createWhatsAppOrder.fulfilled,  (s, a) => {
        s.loading = false;
        s.success = true;
        const order = a.payload?.order;
        if (order) {
          s.orders.unshift(order);
          s.currentOrder = order;
          s.totalOrders += 1;
        }
      })
      .addCase(createWhatsAppOrder.rejected,   (s, a) => { s.loading = false; s.error = a.payload?.message || 'Failed to create order'; })

      // Fetch All Orders
      .addCase(fetchAllOrders.pending,    (s) => { s.loading = true; s.error = null; })
      .addCase(fetchAllOrders.fulfilled,  (s, a) => {
        s.loading = false;
        s.orders      = a.payload.orders      || [];
        s.totalOrders = a.payload.totalOrders || 0;
        s.totalPages  = a.payload.totalPages  || 1;
        s.currentPage = a.payload.currentPage || 1;
      })
      .addCase(fetchAllOrders.rejected,   (s, a) => { s.loading = false; s.error = a.payload?.message || 'Failed to fetch orders'; })

      // Fetch Order By ID
      .addCase(fetchOrderById.pending,    (s) => { s.loading = true; s.error = null; })
      .addCase(fetchOrderById.fulfilled,  (s, a) => { s.loading = false; s.currentOrder = a.payload.order; })
      .addCase(fetchOrderById.rejected,   (s, a) => { s.loading = false; s.error = a.payload?.message || 'Failed to fetch order'; })

      // Change Order Status
      .addCase(changeOrderStatus.pending,    (s) => { s.loading = true; s.error = null; s.success = false; })
      .addCase(changeOrderStatus.fulfilled,  (s, a) => {
        s.loading = false; s.success = true;
        const idx = s.orders.findIndex((o) => o._id === a.payload.order?._id);
        if (idx !== -1) s.orders[idx] = a.payload.order;
        if (s.currentOrder?._id === a.payload.order?._id) s.currentOrder = a.payload.order;
      })
      .addCase(changeOrderStatus.rejected,   (s, a) => { s.loading = false; s.error = a.payload?.message || 'Failed to update order status'; })

      // Update Order Details
      .addCase(updateOrderDetails.pending,    (s) => { s.loading = true; s.error = null; s.success = false; })
      .addCase(updateOrderDetails.fulfilled,  (s, a) => {
        s.loading = false; s.success = true;
        const idx = s.orders.findIndex((o) => o._id === a.payload.order?._id);
        if (idx !== -1) s.orders[idx] = a.payload.order;
        s.currentOrder = a.payload.order;
      })
      .addCase(updateOrderDetails.rejected,   (s, a) => { s.loading = false; s.error = a.payload?.message || 'Failed to update order'; })

      // Cancel Order
      .addCase(cancelOrder.pending,    (s) => { s.loading = true; s.error = null; s.success = false; })
      .addCase(cancelOrder.fulfilled,  (s, a) => {
        s.loading = false; s.success = true;
        const idx = s.orders.findIndex((o) => o._id === a.payload.orderId);
        if (idx !== -1) s.orders[idx].status = 'CANCELLED';
        if (s.currentOrder?._id === a.payload.orderId) s.currentOrder.status = 'CANCELLED';
      })
      .addCase(cancelOrder.rejected,   (s, a) => { s.loading = false; s.error = a.payload?.message || 'Failed to cancel order'; })

      // Fetch Stats
      .addCase(fetchOrderStats.pending,    (s) => { s.loading = true; s.error = null; })
      .addCase(fetchOrderStats.fulfilled,  (s, a) => { s.loading = false; s.stats = a.payload.stats; })
      .addCase(fetchOrderStats.rejected,   (s, a) => { s.loading = false; s.error = a.payload?.message || 'Failed to fetch statistics'; })

      // Fetch by Date Range
      .addCase(fetchOrdersByDateRange.pending,    (s) => { s.loading = true; s.error = null; })
      .addCase(fetchOrdersByDateRange.fulfilled,  (s, a) => {
        s.loading = false;
        s.orders      = a.payload.orders      || [];
        s.totalOrders = a.payload.totalOrders || 0;
      })
      .addCase(fetchOrdersByDateRange.rejected,   (s, a) => { s.loading = false; s.error = a.payload?.message || 'Failed to fetch orders'; });
  }
});

export const {
  clearError,
  clearSuccess,
  setFilters,
  resetFilters,
  clearCurrentOrder,
  resetOrderState,
} = orderSlice.actions;

export default orderSlice.reducer;